import { ComponentRendererProps, SectionComponent } from "@/store/sagas/types/deviceUnlock";
import React from "react";
import dynamic from "next/dynamic";
import sanitizeHtml from '@/lib/utils/sanitizeHtml';

const LazyCTARenderer = dynamic(() => import("./CTARenderer"), {
    loading: () => null
});

const FONT_FAMILY = 'Verizon NHG DS, Helvetica Neue, Helvetica, Arial, sans-serif';

const linkStyles = `
    .hero-link a {
      color: black;
      text-decoration: underline;
      text-underline-offset: 4px;
      font-weight: 400; /* Regular weight matches description */
    }
    .hero-link a:hover {
      opacity: 0.8;
    }
  `;

type HeroContentProps = {
    title: string;
    description: string;
    linkSection?: SectionComponent;
    sanitizedLinkHtml: string;
    onNavigate: ComponentRendererProps["onNavigate"];
};

const HeroContent: React.FC<HeroContentProps> = ({ title, description, linkSection, sanitizedLinkHtml, onNavigate }) => (
    <>
        {title && (
            <h1
                className="text-4xl md:text-5xl font-extrabold text-black mb-8 tracking-tight"
                style={{ fontFamily: FONT_FAMILY }}
            >
                {title}
            </h1>
        )}

        {description && (
            <p
                className="text-base md:text-lg text-black mb-8 leading-relaxed max-w-4xl mx-auto"
                style={{ fontFamily: FONT_FAMILY }}
            >
                {description}
            </p>
        )}

        {linkSection && (
            <div
                className="hero-link text-base md:text-lg text-black"
                style={{
                    fontFamily: FONT_FAMILY
                }}
            >
                {linkSection.cta ? (
                    <>
                        {linkSection.cta.prefix && <span>{linkSection.cta.prefix + " "}</span>}
                        <LazyCTARenderer cta={linkSection.cta} onNavigate={onNavigate} />
                    </>
                ) : (
                    <div dangerouslySetInnerHTML={{ __html: sanitizedLinkHtml }} />
                )}
            </div>
        )}
    </>
);

const MemoizedHeroContent = React.memo(
    HeroContent,
    (prevProps, nextProps) =>
        prevProps.title === nextProps.title
        && prevProps.description === nextProps.description
        && prevProps.linkSection === nextProps.linkSection
        && prevProps.sanitizedLinkHtml === nextProps.sanitizedLinkHtml
        && prevProps.onNavigate === nextProps.onNavigate
);

// Hero Component Renderer (Parent)
const HeroRenderer: React.FC<ComponentRendererProps> = ({ component, onNavigate }) => {
    const { title, description, linkSection } = React.useMemo(() => {
        const sections = component.details?.content?.section || [];

        return {
            title: sections.find((section) => section.id === 'title')?.value || '',
            description: sections.find((section) => section.id === 'description')?.value || '',
            linkSection: sections.find((section) => section.id === 'link')
        };
    }, [component.details?.content?.section]);

    const sanitizedLinkHtml = React.useMemo(() => {
        if (!linkSection || linkSection.cta) {
            return '';
        }

        return sanitizeHtml(linkSection.value || '');
    }, [linkSection]);

    const navigateRef = React.useRef(onNavigate);

    React.useEffect(() => {
        navigateRef.current = onNavigate;
    }, [onNavigate]);

    const stableOnNavigate = React.useCallback((destination: string) => {
        navigateRef.current?.(destination);
    }, []);


    return (
        <>
            <style>{linkStyles}</style>
            <div className="w-full max-w-5xl mx-auto px-4 py-12 text-center">
                <MemoizedHeroContent
                    title={title}
                    description={description}
                    linkSection={linkSection}
                    sanitizedLinkHtml={sanitizedLinkHtml}
                    onNavigate={stableOnNavigate}
                />
            </div>
        </>
    );
};

export default React.memo(HeroRenderer);